import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { usePageTitle } from 'utils/pageTitle';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';
import { openModal } from 'reducers/modalReducer';
import {
	fetchPortfolios,
	deletePortfolio,
} from 'reducers/portfoliosManagementReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import OrganizationGuard from 'components/Organizations/OrganizationGuard';
import PortfolioTable from './PortfolioTable';
import Spinner from 'components/Spinner';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import './PortfolioPage.css';
import _ from 'lodash';

const PortfolioPage = () => {
	usePageTitle('Portfolio Management');
	const dispatch = useDispatch();
	const history = useHistory();
	const [searchQuery, setSearchQuery] = useState('');

	// Get portfolios data from Redux store
	const {
		portfolios,
		isLoading,
		error,
		isDeleting,
		totalCount,
		currentPage,
		perPage,
	} = useSelector((state) => state.portfoliosManagement);

	// Fetch portfolios on component mount
	useEffect(() => {
		dispatch(fetchPortfolios(1, 10));
	}, [dispatch]);

	const displayPortfolios = useMemo(() => {
		return _.orderBy(portfolios, ['createdAt', 'created_at'], ['desc', 'desc']) || [];
	}, [portfolios]);

	// Filter by search query (name or company)
	const filteredPortfolios = useMemo(() => {
		if (!searchQuery.trim()) return displayPortfolios;
		const query = searchQuery.toLowerCase().trim();
		return displayPortfolios.filter((p) => {
			const nameMatch = p.name && p.name.toLowerCase().includes(query);
			const companyName = p.company || p.companyName || p.organization?.name;
			const companyMatch = companyName && companyName.toLowerCase().includes(query);
			return nameMatch || companyMatch;
		});
	}, [displayPortfolios, searchQuery]);

	const handleRefresh = () => {
		dispatch(fetchPortfolios(currentPage || 1, perPage || 10));
	};

	const handleAddPortfolio = () => {
		dispatch(openModal(MODAL_TYPES.ADD_PORTFOLIO_MODAL));
	};

	const handleViewPortfolio = (portfolioId) => {
		history.push(`/dashboard/portfolio/${portfolioId}`);
	};

	const handleEditPortfolio = (portfolioId) => {
		dispatch(openModal(MODAL_TYPES.EDIT_PORTFOLIO_MODAL, { portfolioId }));
	};

	const handleDeletePortfolio = (portfolioId) => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Delete Portfolio',
				onConfirm: async () => {
					try {
						await dispatch(deletePortfolio(portfolioId));
					} catch (err) {
						console.error('Error deleting portfolio:', err);
					}
				},
				children: (
					<div>
						Are you sure you want to delete this portfolio? This action cannot
						be undone.
					</div>
				),
				confirmLabel: 'Delete',
			}),
		);
	};

	return (
		<DashboardLayout>
			<div className="portfolio-management violerts-v3-root">
				<div className="portfolio-card-container">
					{/* Header Row: Title + Centered Search Box + Create Portfolio Button */}
					<div className="portfolio-header-row">
						<div className="portfolio-header-left">
							<h1 className="portfolio-card-title">All Portfolios</h1>
						</div>

						<div className="portfolio-header-center">
							<div className="portfolio-search-box">
								<SearchIcon className="portfolio-search-icon" aria-hidden="true" />
								<input
									type="text"
									placeholder="Search"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="portfolio-search-input"
								/>
							</div>
						</div>

						<div className="portfolio-header-right">
							<OrganizationGuard
								deny="viewer"
								showTooltip
								tooltipMessage="Viewers cannot create portfolios. Contact your organization admin for access."
							>
								<button
									type="button"
									className="portfolio-create-btn"
									onClick={handleAddPortfolio}
								>
									Create Portfolio +
								</button>
							</OrganizationGuard>
						</div>
					</div>

					{/* Portfolio Content */}
					{isLoading ? (
						<div className="portfolio-loading">
							<Spinner />
						</div>
					) : error ? (
						<div className="portfolio-error-banner">
							<span>Error loading portfolios.</span>
							<button
								type="button"
								className="portfolio-retry-btn"
								onClick={handleRefresh}
							>
								Try again
							</button>
						</div>
					) : filteredPortfolios.length === 0 ? (
						<div className="portfolio-empty-state">
							<p className="portfolio-empty-title">No Portfolios Found</p>
							{searchQuery ? (
								<p className="portfolio-empty-subtitle">
									No portfolios match &ldquo;{searchQuery}&rdquo;
								</p>
							) : (
								<>
									<p className="portfolio-empty-subtitle">
										You haven&apos;t created any portfolios yet.
									</p>
									<OrganizationGuard
										deny="viewer"
										fallback={
											<p className="text-muted">
												Contact your organization admin to create portfolios.
											</p>
										}
									>
										<button
											type="button"
											className="portfolio-create-btn"
											onClick={handleAddPortfolio}
										>
											Create Portfolio +
										</button>
									</OrganizationGuard>
								</>
							)}
						</div>
					) : (
						<>
							{/* Portfolio Table */}
							<PortfolioTable
								portfolios={filteredPortfolios}
								onView={handleViewPortfolio}
								onEdit={handleEditPortfolio}
								onDelete={handleDeletePortfolio}
								isDeleting={isDeleting}
							/>

							{/* Pagination Footer */}
							<div className="portfolio-pagination-footer">
								<div className="portfolio-pagination-info">
									{totalCount ? (
										<>
											Showing{' '}
											{Math.min(
												(currentPage - 1) * (perPage || 10) + 1,
												totalCount,
											)}{' '}
											to {Math.min(currentPage * (perPage || 10), totalCount)} of{' '}
											{totalCount} portfolios
										</>
									) : (
										`Showing ${filteredPortfolios.length} portfolios`
									)}
								</div>
								{totalCount && Math.ceil(totalCount / (perPage || 10)) > 1 && (
									<PaginatorV3
										currentPage={currentPage || 1}
										totalPages={Math.ceil(totalCount / (perPage || 10))}
										onPageChange={(page) =>
											dispatch(fetchPortfolios(page, perPage || 10))
										}
									/>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
};

export default PortfolioPage;
